import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Heading, VStack, HStack, Input, Select, Button, Text, Flex, Progress, useToast, Checkbox, CheckboxGroup, SimpleGrid, Icon, Alert, AlertIcon, AlertTitle, AlertDescription, Stack
} from '@chakra-ui/react';
import { FaFileCsv, FaCheckCircle, FaExclamationTriangle, FaDownload } from 'react-icons/fa';
import { startVendaExport, getExportStatus, downloadExportFile } from "../services/api";

const ALL_COLUMNS = [
    'id',
    'id_origem',
    'pack_id_ml',
    'conta',
    'origem',
    'status_taga',
    'status',
    'data_venda',
    'marcador',
    'user_responsavel',
    'valor_total',
    'sku',
    'fornecedor',
    'furo_estoque',
    'furo_motivo',
    'conferencia_status',
    'conferencia_data',
    'conferencia_problemas',
    'conferencia_problema_data',
    'conferencia_unidades',
    'conferencia_observacao'
];

// A conferencia e registrada por item, entao essas colunas exigem o detalhamento por produto
const COLUNAS_CONFERENCIA = [
    'conferencia_status',
    'conferencia_data',
    'conferencia_problemas',
    'conferencia_problema_data',
    'conferencia_unidades',
    'conferencia_observacao'
];

const COLUMN_LABELS = {
    user_responsavel: 'VENDEDOR',
    conferencia_status: 'STATUS DA CONFERÊNCIA',
    conferencia_data: 'DATA DA CONFERÊNCIA',
    conferencia_problemas: 'PROBLEMAS APONTADOS',
    conferencia_problema_data: 'DATA DO PROBLEMA',
    conferencia_unidades: 'UNIDADES CONFERIDAS',
    conferencia_observacao: 'OBSERVAÇÃO DA CONFERÊNCIA'
};

const getColumnLabel = (col) => COLUMN_LABELS[col] || col.replace(/_/g, ' ').toUpperCase();


const ALL_STATUS = ['confirmadas', 'enviar', 'transito', 'concluida', 'cancelada'];

const ExportarVendas = () => {
    const [selectedColumns, setSelectedColumns] = useState(ALL_COLUMNS);
    const [filters, setFilters] = useState({
        data_inicial: '',
        data_final: '',
        status: ALL_STATUS,
        por_item: false,
    });
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const toast = useToast();
    const intervalRef = useRef(null);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleStatusChange = (selectedStatus) => {
        setFilters(prev => ({
            ...prev,
            status: selectedStatus
        }));
    };

    const temConferencia = (colunas) => colunas.some(c => COLUNAS_CONFERENCIA.includes(c));

    // Colunas de conferencia so existem por item, entao ligam o detalhamento sozinhas
    const handleColumnsChange = (colunas) => {
        setSelectedColumns(colunas);
        if (temConferencia(colunas)) {
            setFilters(prev => ({ ...prev, por_item: true }));
        }
    };

    const handleSelectAllColumns = (e) => {
        handleColumnsChange(e.target.checked ? ALL_COLUMNS : []);
    };

    const handlePorItemChange = (marcado) => {
        setFilters(prev => ({ ...prev, por_item: marcado }));
        if (!marcado) {
            setSelectedColumns(prev => prev.filter(c => !COLUNAS_CONFERENCIA.includes(c)));
        }
    };

    const handleSelectAllStatus = (e) => {
        if (e.target.checked) {
            handleStatusChange(ALL_STATUS);
        } else {
            handleStatusChange([]);
        }
    };

    const handleStartExport = async () => {
        if (selectedColumns.length === 0) {
            toast({
                title: "Nenhuma coluna selecionada",
                description: "Por favor, selecione pelo menos uma coluna para exportar.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        setIsGenerating(true);
        setJobStatus(null);
        setJobId(null);

        try {
            const data = await startVendaExport(selectedColumns, filters);
            setJobId(data.job_id);
            toast({
                title: "Exportação iniciada!",
                description: "O arquivo está sendo gerado em segundo plano. Você será notificado quando estiver pronto.",
                status: "info",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Erro ao iniciar a exportação",
                description: error.response?.data?.errors ? JSON.stringify(error.response.data.errors) : (error.response?.data?.message || "Ocorreu um erro inesperado."),
                status: "error",
                duration: 9000,
                isClosable: true,
            });
            setIsGenerating(false);
        }
    };

    const fetchStatus = useCallback(async () => {
        if (!jobId) return;

        try {
            const statusData = await getExportStatus(jobId);
            setJobStatus(statusData);

            if (['completed', 'failed'].includes(statusData.status)) {
                setIsGenerating(false);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                if (statusData.status === 'completed') {
                    toast({
                        title: "Arquivo Pronto!",
                        description: "Sua exportação de vendas está pronta para download.",
                        status: "success",
                        duration: 7000,
                        isClosable: true,
                    });
                }
            }
        } catch (error) {
            console.error("Erro ao buscar status:", error);
            setIsGenerating(false);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [jobId, toast]);

    useEffect(() => {
        if (isGenerating && jobId) {
            if (!intervalRef.current) {
                intervalRef.current = setInterval(fetchStatus, 5000);
            }
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isGenerating, jobId, fetchStatus]);


    const handleDownload = async () => {
        if (jobStatus?.file_name) {
            try {
                await downloadExportFile(jobStatus.file_name);

                toast({
                    title: "Download Iniciado",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });

                setJobStatus(null);
                setJobId(null);

            } catch (error) {
                toast({
                    title: "Erro no Download",
                    description: "Não foi possível baixar o arquivo. Por favor, gere um novo relatório.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });

                setJobStatus(null);
                setJobId(null);
            }
        }
    }

    const renderStatus = () => {
        if (!jobStatus) return null;
        switch (jobStatus.status) {
            case 'pending':
            case 'running':
                return (
                    <Box w="100%">
                        <Flex justify="space-between" mb={1}>
                            <Text fontSize="sm" fontWeight="medium">{jobStatus.message}</Text>
                            <Text fontSize="sm" fontWeight="bold" color="blue.600">{jobStatus.progress || 0}%</Text>
                        </Flex>
                        <Progress value={jobStatus.progress} size="md" colorScheme="blue" borderRadius="md" isIndeterminate={jobStatus.status === 'pending'} />
                    </Box>
                );
            case 'completed':
                return (
                    <Alert status="success" borderRadius="md" variant="left-accent">
                        <AlertIcon as={FaCheckCircle} />
                        <Box flex="1">
                            <AlertTitle>Arquivo Pronto!</AlertTitle>
                            <AlertDescription>
                                <Button leftIcon={<FaDownload />} colorScheme="green" variant="solid" onClick={handleDownload} size="sm">
                                    Baixar Planilha
                                </Button>
                            </AlertDescription>
                        </Box>
                    </Alert>
                );
            case 'failed':
                return (
                    <Alert status="error" borderRadius="md" variant="left-accent">
                        <AlertIcon as={FaExclamationTriangle} />
                        <Box flex="1">
                            <AlertTitle>Falha na Exportação!</AlertTitle>
                            <AlertDescription fontSize="sm">{jobStatus.message || 'Ocorreu um erro inesperado.'}</AlertDescription>
                        </Box>
                    </Alert>
                );
            default: return null;
        }
    };

    return (
        <Box ml={{ base: 0, lg: '60px' }} p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
            <VStack spacing={6} align="stretch" maxW="5xl" mx="auto">
                <Heading justify="center" color={"#F68633"} size="lg">Exportar Base de Vendas</Heading>

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>1. Selecione as Colunas</Heading>
                    <Checkbox isChecked={selectedColumns.length === ALL_COLUMNS.length} onChange={handleSelectAllColumns} mb={4} fontWeight="bold">
                        Selecionar Todas as Colunas
                    </Checkbox>
                    <CheckboxGroup colorScheme="orange" value={selectedColumns} onChange={handleColumnsChange}>
                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                            {ALL_COLUMNS.map(col => <Checkbox key={col} value={col}>{getColumnLabel(col)}</Checkbox>)}
                        </SimpleGrid>
                    </CheckboxGroup>

                    <Box mt={5} pt={4} borderTop="1px solid" borderColor="gray.100">
                        <Checkbox
                            colorScheme="orange"
                            isChecked={filters.por_item}
                            onChange={(e) => handlePorItemChange(e.target.checked)}
                            fontWeight="bold"
                        >
                            Detalhar por produto (uma linha por item da venda)
                        </Checkbox>
                        <Text fontSize="xs" color="gray.500" mt={1} ml={6}>
                            Adiciona as colunas Código Global, Produto, Quantidade, Valor Unitário e Total do Item.
                            Vendas com mais de um produto geram uma linha para cada. SKU e Fornecedor passam a ser do item.
                        </Text>
                        {temConferencia(selectedColumns) && (
                            <Text fontSize="xs" color="orange.600" mt={2} ml={6} fontWeight="medium">
                                As colunas de conferência são registradas por item, por isso o detalhamento por produto
                                foi marcado automaticamente. Ao desmarcá-lo, essas colunas saem da seleção.
                            </Text>
                        )}
                    </Box>
                </Box>

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>2. Aplique Filtros (Opcional)</Heading>
                    <Stack spacing={6}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Box>
                                <Text mb={1} fontSize="sm" fontWeight="medium">Data Inicial</Text>
                                <Input
                                    type="date"
                                    name="data_inicial"
                                    value={filters.data_inicial}
                                    onChange={handleFilterChange}
                                />
                            </Box>
                            <Box>
                                <Text mb={1} fontSize="sm" fontWeight="medium">Data Final</Text>
                                <Input
                                    type="date"
                                    name="data_final"
                                    value={filters.data_final}
                                    onChange={handleFilterChange}
                                />
                            </Box>
                        </SimpleGrid>

                        <Box>
                            <Heading size="sm" mb={3}>Status da Venda</Heading>
                            <Checkbox
                                colorScheme="orange"
                                isChecked={filters.status.length === ALL_STATUS.length}
                                onChange={handleSelectAllStatus}
                                mb={3}
                                fontWeight="bold"
                            >
                                Selecionar Todos os Status
                            </Checkbox>
                            <CheckboxGroup colorScheme="orange" value={filters.status} onChange={handleStatusChange}>
                                <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
                                    {ALL_STATUS.map(s => (
                                        <Checkbox key={s} value={s} textTransform="capitalize">{s}</Checkbox>
                                    ))}
                                </SimpleGrid>
                            </CheckboxGroup>
                        </Box>
                    </Stack>
                </Box>

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>3. Gere o Arquivo</Heading>
                    <VStack spacing={4}>
                        <Button leftIcon={<Icon as={FaFileCsv} />} colorScheme="orange" onClick={handleStartExport} isLoading={isGenerating} loadingText="Gerando..." isDisabled={isGenerating} w="full" size="lg">
                            Gerar Arquivo de Exportação
                        </Button>
                        {renderStatus()}
                    </VStack>
                </Box>
            </VStack>
        </Box>
    );
};

export default ExportarVendas;
